(() => {
  'use strict';
  if (customElements.get('installer-finder')) return;
  const core = window.InstallerFinderCore;
  const element = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };
  const arrow = (text = '↗') => { const node = element('span', '', text); node.setAttribute('aria-hidden', 'true'); return node; };
  const button = (className, text, action, value = '') => {
    const node = element('button', className, text);
    node.type = 'button'; node.setAttribute(action, value); return node;
  };
  const link = (text, href, className = '') => {
    const node = element('a', className, text); node.href = href;
    node.target = '_blank'; node.rel = 'noopener noreferrer'; return node;
  };

  class InstallerFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized || !core) return;
      const source = this.querySelector('[data-finder-profiles]');
      if (!source) return;
      try {
        this.states = JSON.parse(this.querySelector('[data-finder-states]').textContent);
        this.profiles = core.normalizeProfiles(JSON.parse(source.textContent), this.states, JSON.parse(this.querySelector('[data-finder-overrides]').textContent));
      } catch {
        this.querySelector('[data-search-feedback]').hidden = false;
        this.querySelector('[data-search-feedback]').textContent = 'The directory is temporarily unavailable. Please contact our team for help with your area.';
        this.querySelector('[data-finder-empty]').hidden = false;
        this.querySelector('[data-request-general]').addEventListener('click', () => { window.location.href = '/pages/contact'; });
        return;
      }
      this.initialized = true;
      this.filters = { state: 'all', query: '', specialty: 'all', origin: null, radius: 50 };
      this.limit = 8;
      this.queryVersion = 0;
      this.input = this.querySelector('[data-finder-search]');
      this.stateSelect = this.querySelector('[data-finder-state]');
      this.cards = this.querySelector('[data-finder-cards]');
      this.workspace = this.querySelector('[data-finder-workspace]');
      this.profileDialog = this.querySelector('[data-profile-dialog]');
      this.requestDialog = this.querySelector('[data-request-dialog]');
      const applicants = this.profiles.filter(profile => profile.status === 'applicant');
      if (applicants.length) {
        this.querySelector('[data-applicant-notice]').hidden = false;
        this.querySelector('[data-applicant-summary]').textContent = applicants.length + ' previous US applicants.';
        this.querySelector('[data-map-key-label]').textContent = 'Profiles to explore';
      }
      this.hasApplicants = applicants.length > 0;
      this.initializeMap();
      this.addEventListener('click', event => this.onClick(event));
      this.addEventListener('keydown', event => this.onKeydown(event));
      this.querySelector('[data-finder-search-form]').addEventListener('submit', event => { event.preventDefault(); this.runSearch(); });
      this.input.addEventListener('input', () => {
        this.queryVersion++;
        clearTimeout(this.searchTimer);
        this.searchTimer = setTimeout(() => this.runSearch(false), 220);
      });
      this.stateSelect.addEventListener('change', () => this.chooseState(this.stateSelect.value));
      this.querySelector('[data-finder-radius]').addEventListener('change', event => {
        this.filters.radius = Number(event.target.value); this.limit = 8; this.render();
      });
      [this.profileDialog, this.requestDialog].forEach(dialog => {
        dialog.addEventListener('click', event => {
          if (event.target !== dialog) return;
          const rect = dialog.getBoundingClientRect();
          if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
        });
        dialog.addEventListener('close', () => {
          if (!this.profileDialog.open && !this.requestDialog.open) {
            this.unlockScroll();
            if (this.returnFocus?.isConnected) this.returnFocus.focus();
          }
        });
      });
      this.render();
      let slug = '';
      try { slug = decodeURIComponent(window.location.hash.slice(1)); } catch { /* Ignore malformed profile links. */ }
      const selected = this.profiles.find(profile => 'installer-' + profile.slug === slug);
      if (selected) this.openProfile(selected);
      const params = new URLSearchParams(window.location.search);
      const requested = this.profiles.find(profile => profile.slug === params.get('installer'));
      if (requested && params.get('request') === '1') this.openRequest(requested);
      if (this.querySelector('[data-request-success], .finder-form-error')) this.showDialog(this.requestDialog);
    }

    disconnectedCallback() {
      clearTimeout(this.searchTimer);
      this.queryVersion++;
      this.unlockScroll();
    }

    initializeMap() {
      const shapes = [...this.querySelectorAll('[data-map-state]')];
      shapes.forEach((shape, index) => {
        const code = shape.dataset.mapState;
        const count = this.profiles.filter(profile => profile.state === code || profile.areas.some(area => area.state === code)).length;
        const name = this.stateName(code);
        const label = name + ', ' + count + (count === 1 ? ' profile' : ' profiles');
        shape.classList.toggle('has-profiles', count > 0);
        shape.classList.toggle('has-many-profiles', count >= 10);
        shape.setAttribute('aria-label', label);
        shape.querySelector('title').textContent = label;
        shape.setAttribute('tabindex', index === 0 ? '0' : '-1');
      });
    }

    stateName(code) { return this.states.find(state => state.code === code)?.name || code; }

    onClick(event) {
      const target = event.target.closest('button, [data-map-state], [data-open-profile]');
      if (!target) return;
      if (target.hasAttribute('data-map-state')) this.chooseState(target.dataset.mapState);
      if (target.hasAttribute('data-show-all-states')) this.chooseState('all');
      if (target.hasAttribute('data-clear-filters')) this.resetFilters();
      if (target.hasAttribute('data-specialty')) {
        this.filters.specialty = target.dataset.specialty;
        this.limit = 8; this.render();
      }
      if (target.hasAttribute('data-finder-view')) {
        const list = target.dataset.finderView === 'list';
        this.workspace.classList.toggle('is-list', list);
        this.querySelector('[data-finder-map-panel]').hidden = list;
        this.querySelectorAll('[data-finder-view]').forEach(item => item.setAttribute('aria-pressed', String(item === target)));
      }
      if (target.hasAttribute('data-open-profile')) {
        if (target.tagName === 'A' && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0)) return;
        event.preventDefault();
        const profile = this.profiles.find(item => item.id === target.dataset.openProfile);
        if (profile) { this.returnFocus = target; this.openProfile(profile); }
      }
      if (target.hasAttribute('data-request-profile')) {
        const profile = this.profiles.find(item => item.id === target.dataset.requestProfile);
        if (profile) {
          if (!this.profileDialog.open) this.returnFocus = target;
          this.openRequest(profile);
        }
      }
      if (target.hasAttribute('data-request-general')) { this.returnFocus = target; this.openRequest(); }
      if (target.hasAttribute('data-close-dialog')) target.closest('dialog').close();
      if (target.hasAttribute('data-load-more')) {
        const previous = this.limit;
        this.limit += 8; this.render(false);
        this.cards.children[previous]?.querySelector('a, button')?.focus();
      }
    }

    onKeydown(event) {
      const shape = event.target.closest('[data-map-state]');
      if (!shape) return;
      if (['Enter', ' '].includes(event.key)) {
        event.preventDefault(); this.chooseState(shape.dataset.mapState); return;
      }
      if (!['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      const shapes = [...this.querySelectorAll('[data-map-state]')];
      let index = shapes.indexOf(shape);
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = shapes.length - 1;
      else index = (index + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1) + shapes.length) % shapes.length;
      shapes.forEach(item => item.setAttribute('tabindex', '-1'));
      shapes[index].setAttribute('tabindex', '0'); shapes[index].focus();
    }

    setFeedback(message) {
      const feedback = this.querySelector('[data-search-feedback]');
      feedback.textContent = message;
      feedback.hidden = !message;
    }

    async getPostalLookup() {
      if (this.postalLookup) return this.postalLookup;
      if (!this.postalPromise) {
        this.postalPromise = (async () => {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 15000);
          try {
            const response = await fetch(this.dataset.zipUrl, { signal: controller.signal, credentials: 'same-origin' });
            if (!response.ok) throw new Error('ZIP data unavailable');
            this.postalLookup = core.buildPostalLookup(await response.json());
            return this.postalLookup;
          } finally { clearTimeout(timeout); }
        })().catch(error => { this.postalPromise = null; throw error; });
      }
      return this.postalPromise;
    }

    setBusy(busy) {
      this.querySelector('.finder-search-button').disabled = busy;
      this.querySelector('[data-finder-search-form]').setAttribute('aria-busy', String(busy));
    }

    async runSearch(submitted = true) {
      clearTimeout(this.searchTimer);
      const version = ++this.queryVersion;
      const raw = this.input.value.trim();
      this.filters.origin = null; this.filters.query = ''; this.filters.state = 'all';
      this.stateSelect.value = 'all'; this.limit = 8;
      const zip = core.postalCode(raw);
      if (zip) {
        this.setFeedback('Finding businesses near ZIP ' + zip + '…'); this.setBusy(true);
        try {
          const lookup = await this.getPostalLookup();
          if (version !== this.queryVersion || !this.isConnected) return;
          const origin = lookup.zip(zip);
          if (!origin) {
            this.setFeedback('That ZIP code is not in our location data. Try a nearby ZIP, city, or state.');
          } else {
            this.filters.origin = origin; this.setFeedback('');
          }
          this.render();
        } catch {
          if (version !== this.queryVersion) return;
          this.setFeedback('ZIP lookup is temporarily unavailable. You can still search by city, business, or state.');
          this.render();
        } finally {
          if (version === this.queryVersion) this.setBusy(false);
        }
        return;
      }
      this.setBusy(false);
      if (raw && /^[\d\s-]+$/.test(raw)) {
        this.setFeedback(submitted ? 'Enter a five-digit US ZIP code, such as 78613.' : 'Enter all five digits of your ZIP code.');
      } else {
        this.setFeedback('');
        const state = this.states.find(item => [item.code.toLowerCase(), item.name.toLowerCase()].includes(raw.toLowerCase()));
        if (state) { this.filters.state = state.code; this.stateSelect.value = state.code; }
        else this.filters.query = raw;
      }
      this.render();
    }

    chooseState(code) {
      this.queryVersion++; clearTimeout(this.searchTimer); this.setBusy(false);
      this.filters.state = this.states.some(state => state.code === code) ? code : 'all';
      this.filters.query = ''; this.filters.origin = null;
      this.input.value = ''; this.stateSelect.value = this.filters.state;
      this.setFeedback(''); this.limit = 8; this.render();
    }

    resetFilters() {
      this.filters.specialty = 'all'; this.filters.radius = 50;
      this.querySelector('[data-finder-radius]').value = '50';
      this.chooseState('all');
    }

    render(resetScroll = true) {
      const matches = core.filterProfiles(this.profiles, this.filters, this.postalLookup);
      const { state, specialty, origin, query, radius } = this.filters;
      const place = origin ? origin.city + ', ' + origin.state : state !== 'all' ? this.stateName(state) : query ? 'Search results' : 'Across the United States';
      this.querySelector('[data-results-title]').textContent = place;
      this.querySelector('[data-result-count]').textContent = matches.length;
      this.querySelector('[data-results-eyebrow]').textContent = origin ? 'NEAR YOUR PROJECT' : 'EXPLORE THE DIRECTORY';
      const noun = this.hasApplicants ? 'business' : 'installer';
      this.querySelector('[data-results-note]').textContent = matches.length + ' ' + noun + (matches.length === 1 ? '' : 'es') + (origin ? ' within ' + radius + ' miles. Confirm service coverage.' : ' to explore. ' + (specialty !== 'all' ? 'Only profiles with a listed specialty are shown.' : 'Select a profile to learn more.'));
      if (!this.hasApplicants && matches.length !== 1) this.querySelector('[data-results-note]').textContent = this.querySelector('[data-results-note]').textContent.replace('installeres', 'installers');
      this.querySelectorAll('[data-specialty]').forEach(item => item.setAttribute('aria-pressed', String(item.dataset.specialty === specialty)));
      const summary = [];
      if (origin) summary.push('Within ' + radius + ' miles of ' + origin.zip + ' · ' + origin.city + ', ' + origin.state);
      else if (state !== 'all') summary.push(this.stateName(state));
      if (query) summary.push('“' + query + '”');
      if (specialty !== 'all') summary.push(core.labels[specialty]);
      this.querySelector('[data-active-filters]').hidden = !summary.length;
      this.querySelector('[data-filter-summary]').textContent = summary.join(' · ');
      this.querySelector('[data-radius-control]').hidden = !origin;
      this.querySelector('[data-map-region-label]').textContent = state !== 'all' ? this.stateName(state) : origin ? 'Near ' + origin.zip : 'Explore all 50 states';
      const shapes = [...this.querySelectorAll('[data-map-state]')];
      shapes.forEach((shape, index) => {
        const selected = shape.dataset.mapState === (origin?.state || state);
        shape.setAttribute('aria-pressed', String(selected));
        shape.setAttribute('tabindex', selected || state === 'all' && !origin && index === 0 ? '0' : '-1');
      });
      this.cards.replaceChildren(...matches.slice(0, this.limit).map(match => this.makeCard(match)));
      this.querySelector('[data-finder-empty]').hidden = matches.length > 0;
      this.querySelector('[data-empty-title]').textContent = this.profiles.length && (query || specialty !== 'all' || origin) ? 'Let’s widen the search.' : 'Your project still starts here.';
      this.querySelector('[data-empty-description]').textContent = this.profiles.length && (query || specialty !== 'all' || origin)
        ? 'No profiles match these filters yet. Try another specialty, a nearby city, or a wider search radius. Our team can also help you explore installation options.'
        : 'There are no published profiles in this area yet. Tell us where you are building and our team can help you explore the next step.';
      this.querySelector('[data-load-more]').hidden = matches.length <= this.limit;
      this.querySelector('[data-proximity-note]').textContent = origin
        ? 'Distances are approximate from ZIP or city centers, not driving routes. Proximity does not confirm an installer’s service area.'
        : 'Installers are independent businesses. Confirm services, coverage, and availability before booking.';
      this.querySelector('[data-finder-announcement]').textContent = matches.length + ' matching profiles. ' + place + (specialty !== 'all' ? '. ' + core.labels[specialty] : '');
      if (resetScroll) this.querySelector('.finder-results-panel').scrollTop = 0;
    }

    monogram(profile) {
      const words = profile.name.split(/\s+/).filter(word => /^[a-z0-9]/i.test(word));
      const initials = words.slice(0, 2).map(word => word[0]).join('').toUpperCase();
      const node = element('span', 'finder-monogram', initials);
      node.dataset.tone = String(profile.name.charCodeAt(0) % 3);
      node.setAttribute('aria-hidden', 'true'); return node;
    }

    makeCard({ profile, distance }) {
      const card = element('article', 'finder-card');
      card.dataset.profileId = profile.id;
      const heading = element('div', 'finder-card-heading');
      if (profile.image) {
        const photo = element('img', 'finder-card-photo');
        photo.src = profile.image; photo.alt = profile.imageAlt; photo.width = 136; photo.height = 124; photo.loading = 'lazy';
        photo.addEventListener('error', () => photo.replaceWith(this.monogram(profile)), { once: true });
        heading.append(photo);
      } else heading.append(this.monogram(profile));
      const info = element('div');
      const title = element('h3');
      const profileLink = element('a', '', profile.name); profileLink.href = profile.profileUrl;
      if (profile.openInDialog) profileLink.dataset.openProfile = profile.id;
      title.append(profileLink);
      const distanceLabel = distance == null ? '' : ' · ' + (distance < 1 ? 'Less than 1 mile' : Math.round(distance) + ' miles') + ' away';
      info.append(title, element('p', 'finder-card-location', (profile.hasBusinessLocation ? 'Based in ' : 'Serves ') + (profile.city ? profile.city + ', ' : '') + profile.state + distanceLabel));
      info.append(element('span', 'finder-card-status' + (profile.status === 'approved' ? ' is-approved' : ''), profile.status === 'approved' ? 'Network member' : 'Previous applicant · Pending confirmation'));
      heading.append(info); card.append(heading);
      const areaQuery = core.normalize(this.filters.query);
      const areaPriority = area => Number(Boolean(areaQuery) && core.normalize(this.areaLabel(area)).includes(areaQuery)) * 2 + Number(area.state === this.filters.state);
      const listedAreas = [...profile.areas].sort((a, b) => areaPriority(b) - areaPriority(a));
      const coverage = profile.areas.length
        ? 'Serves: ' + listedAreas.slice(0, 2).map(area => this.areaLabel(area)).join(' · ') + (profile.areas.length > 2 ? ' +' + (profile.areas.length - 2) + ' more' : '')
        : 'Service area awaiting confirmation';
      card.append(element('p', 'finder-card-coverage', coverage));
      const tags = element('div', 'finder-card-tags');
      if (profile.specialties.length) profile.specialties.forEach(type => tags.append(element('span', '', core.labels[type])));
      else tags.append(element('span', 'finder-tag-unspecified', 'Specialties to be confirmed'));
      card.append(tags);
      const actions = element('div', 'finder-card-actions');
      const details = element('a', '', 'View profile '); details.href = profile.profileUrl; details.append(arrow());
      if (profile.openInDialog) details.dataset.openProfile = profile.id;
      const request = button('', profile.status === 'approved' ? 'Request installation quote ' : 'Ask about this installer ', 'data-request-profile', profile.id); request.append(arrow('→'));
      actions.append(details, request); card.append(actions); return card;
    }

    areaLabel(area) {
      return area.label ? area.label + (area.kind === 'state' ? '' : ', ' + area.state) : this.stateName(area.state);
    }

    showDialog(dialog) {
      if (this.savedOverflow == null) { this.savedOverflow = document.body.style.overflow; document.body.style.overflow = 'hidden'; }
      if (!dialog.open) dialog.showModal();
    }

    unlockScroll() {
      if (this.savedOverflow != null) { document.body.style.overflow = this.savedOverflow; this.savedOverflow = null; }
    }

    openProfile(profile) {
      const content = this.querySelector('[data-profile-content]'); content.replaceChildren();
      const top = element('div', 'finder-profile-top');
      top.append(this.monogram(profile));
      const badge = element('div'); badge.append(element('p', 'finder-eyebrow', 'INDEPENDENT INSTALLATION BUSINESS'), element('p', 'finder-card-location', profile.trade || profile.city + ', ' + profile.state));
      top.append(badge); content.append(top);
      const title = element('h2', '', profile.name); title.id = 'finder-profile-name-' + this.dataset.sectionId; content.append(title);
      content.append(element('p', 'finder-profile-location', (profile.hasBusinessLocation ? 'Based in ' : 'Service area: ') + (profile.city ? profile.city + ', ' : '') + this.stateName(profile.state)));
      content.append(element('div', 'finder-profile-status' + (profile.status === 'approved' ? ' is-approved' : ''), profile.status === 'approved'
        ? 'Network member. This business has completed the network application review. Confirm current credentials, services, and availability before booking.'
        : 'Previously applied to our installer program. Current participation, services, and availability still need to be confirmed.'));
      if (profile.image) {
        const photo = element('img', 'finder-profile-photo'); photo.src = profile.image; photo.alt = profile.imageAlt; photo.width = 1000; photo.height = 650;
        photo.addEventListener('error', () => photo.remove(), { once: true }); content.append(photo);
      }
      if (profile.description) content.append(element('p', 'finder-dialog-description', profile.description));
      const specialtySection = element('div', 'finder-profile-section'); specialtySection.append(element('h3', '', 'Project specialties'));
      const tags = element('div', 'finder-card-tags');
      if (profile.specialties.length) profile.specialties.forEach(type => tags.append(element('span', '', core.labels[type])));
      else specialtySection.append(element('p', '', 'Project specialties have not been confirmed yet. Tell us what you are planning and our team can help you ask the right questions.'));
      specialtySection.append(tags);
      if (profile.specialtySource) {
        const source = element('p', 'finder-profile-source', 'Service information from the '); source.append(link('business website', profile.specialtySource), document.createTextNode('. Confirm suitability for your glass railing project.')); specialtySection.append(source);
      }
      content.append(specialtySection);
      const areas = element('div', 'finder-profile-section'); areas.append(element('h3', '', 'Service areas'));
      if (profile.areas.length) {
        const list = element('ul'); profile.areas.forEach(area => list.append(element('li', '', this.areaLabel(area)))); areas.append(list);
        if (profile.areaNote) areas.append(element('p', '', profile.areaNote));
        if (profile.areaSource) {
          const source = element('a', 'installer-profile-source', 'View service area source ↗');
          source.href = profile.areaSource; source.target = '_blank'; source.rel = 'noopener noreferrer'; areas.append(source);
        }
        areas.append(element('p', '', 'Coverage can vary by service and project. Confirm your address before booking.'));
      } else areas.append(element('p', '', 'Service area awaiting confirmation. The business location does not establish how far the team travels.'));
      content.append(areas);
      if (profile.website || profile.phone || profile.email || profile.gallery) {
        const contact = element('div', 'finder-profile-section finder-profile-contact'); contact.append(element('h3', '', 'Business details'));
        if (profile.website) contact.append(link('Visit business website ↗', profile.website));
        if (profile.phone && /^[+\d\s().-]+$/.test(profile.phone)) { const phone = element('a', '', profile.phone); phone.href = 'tel:' + profile.phone.replace(/[^+\d]/g, ''); contact.append(phone); }
        if (profile.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profile.email)) { const email = element('a', '', profile.email); email.href = 'mailto:' + profile.email; contact.append(email); }
        if (profile.gallery) contact.append(link('View project gallery ↗', profile.gallery));
        if (!profile.image) contact.append(element('p', 'finder-profile-source', 'Portfolio photos have not been added to this profile yet.'));
        content.append(contact);
      }
      const cta = button('finder-profile-cta', profile.status === 'approved' ? 'Request an installation quote ' : 'Ask about this installer ', 'data-request-profile', profile.id); cta.append(arrow()); content.append(cta);
      content.append(element('p', 'finder-profile-cta-note', 'This request goes to the Glass Railings team. Installation availability is confirmed separately.'));
      this.showDialog(this.profileDialog);
    }

    openRequest(profile) {
      const wasProfileOpen = this.profileDialog.open;
      const context = this.querySelector('[data-request-context]');
      const name = this.querySelector('[data-request-installer]');
      if (name) name.value = profile?.name || 'Help finding an installer';
      const status = this.querySelector('[data-request-status]');
      if (status) status.value = profile ? profile.status === 'approved' ? 'Approved network member' : 'Previous applicant; participation not confirmed' : 'Area inquiry';
      if (context) { context.hidden = false; context.textContent = profile ? 'Regarding: ' + profile.name + ' · ' + profile.city + ', ' + profile.state : 'Help finding installation options in your area.'; }
      this.querySelector('[data-request-title]').textContent = profile?.status === 'approved' ? 'Request an installation quote.' : profile ? 'Ask about this installer.' : 'Find your next step.';
      const state = this.querySelector('[data-request-state]');
      const zip = this.querySelector('[data-request-zip]');
      const type = this.querySelector('[data-request-type]');
      if (state && !state.value) state.value = this.filters.origin?.state || (this.filters.state !== 'all' ? this.filters.state : profile?.state || '');
      if (zip && !zip.value && this.filters.origin) zip.value = this.filters.origin.zip;
      if (type && !type.value && this.filters.specialty !== 'all') type.value = this.filters.specialty;
      this.showDialog(this.requestDialog);
      if (wasProfileOpen) this.profileDialog.close();
    }
  }
  customElements.define('installer-finder', InstallerFinder);
})();
